/**
 * エリア / 路線の集計の表示 (docs/27 §9-3、2026-09-16 オーナー指示)。
 *
 * - 集計する調査項目を**この画面の中で**切り替えられる (以前は一覧ページのセレクタでしか変えられなかった)。
 * - **集計の細かさ**（マスタの既定 / 大分類 / 細分類）を選べる。マスタの色分け設定が大分類のときでも、
 *   細分類（例: 12 の質的基準）ごとの集計にできる。
 * - 表示は「棒グラフ (1 軸)」と「マトリクス (2 軸)」を切り替えられる。マトリクスの行・列はどちらも
 *   調査項目から選べ、既定は「集計する調査項目 × 評価」。
 * - 選択内容は呼出側へ返す (保存するスナップショット・PDF をその内容で作るため)。
 *
 * 特定の地区・テンプレートに依存する分岐は持たない (マスタの行・色分け設定と出力フォーマットのみで動く)。
 */
import { useEffect, useMemo, useState } from 'react';
import type { EvaluationAxisDef } from '@citizen-walk/shared-types';
import { AreaBarChart } from './AreaBarChart';
import {
  computeAxisCounts,
  computeEvaluationSummary,
  expandAxisCountsByPolarity,
  type AxisGranularity,
} from './computeAreaAxisCounts';
import { computeAxisMatrix } from './computeAxisMatrix';
import { EvaluationSummaryLine } from './EvaluationSummaryLine';
import { StreetSurveyAxisMatrix } from './StreetSurveyAxisMatrix';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { ResolvedSurveyAxis } from './useStreetSurveyConfig';
import type { StreetSurveyPost } from './useStreetSurveyPosts';

/** 集計の選択内容 (呼出側が保存・PDF に使う)。 */
export interface AggregationSelection {
  rowAxisKey: string | null;
  colAxisKey: string | null;
  granularity: AxisGranularity;
  view: 'bar' | 'matrix';
  /** 棒グラフで、各項目を評価の区分 (ポジ / ネガ / 中立) ごとの棒に分けるか。 */
  splitByPolarity: boolean;
}

export interface StreetSurveyAggregationPanelProps {
  posts: StreetSurveyPost[];
  /** 選べる調査項目 (実効の出力フォーマット由来)。空なら従来の 1 軸表示のみ。 */
  axes: ResolvedSurveyAxis[];
  evaluation?: EvaluationAxisDef | null;
  /** 行 (棒グラフの軸) の初期値。未指定なら先頭の項目。 */
  initialRowAxisKey?: string | null;
  /** 選択内容が変わったときに呼ぶ (初期値も含めて 1 回は呼ぶ)。 */
  onChange?: (selection: AggregationSelection) => void;
}

export const AXIS_GRANULARITY_LABELS: Record<AxisGranularity, string> = {
  auto: 'マスタの既定',
  major: '大分類でまとめる',
  category: '細分類ごと',
};

/** `computeAxisCounts` が受け取る形へ変換する。 */
export function toPrimaryAxis(axis: ResolvedSurveyAxis | null): PrimaryAxisResolution {
  if (!axis) return { primaryAxis: null, master: null, byCategoryCd: new Map(), loading: false };
  return {
    primaryAxis: axis.axis,
    master: axis.master,
    byCategoryCd: axis.byCategoryCd,
    loading: false,
  };
}

export function StreetSurveyAggregationPanel({
  posts,
  axes,
  evaluation = null,
  initialRowAxisKey = null,
  onChange,
}: StreetSurveyAggregationPanelProps) {
  const evaluationAxisKey = evaluation
    ? (axes.find((a) => a.codeField === evaluation.field)?.key ?? null)
    : null;
  const [view, setView] = useState<'bar' | 'matrix'>('bar');
  // 既定は細分類ごと (オーナー指示 2026-09-16)。
  const [granularity, setGranularity] = useState<AxisGranularity>('category');
  const [splitByPolarity, setSplitByPolarity] = useState(false);
  const [rowAxisKey, setRowAxisKey] = useState<string | null>(
    initialRowAxisKey ?? axes[0]?.key ?? null,
  );
  const [colAxisKey, setColAxisKey] = useState<string | null>(
    evaluationAxisKey ??
      axes.find((a) => a.key !== (initialRowAxisKey ?? axes[0]?.key))?.key ??
      null,
  );

  const rowAxis = axes.find((a) => a.key === rowAxisKey) ?? null;
  const colAxis = axes.find((a) => a.key === colAxisKey) ?? null;

  // 選択内容を呼出側へ通知する (保存・PDF が同じ内容になるように)。
  useEffect(() => {
    onChange?.({ rowAxisKey, colAxisKey, granularity, view, splitByPolarity });
    // onChange の同一性は呼出側に委ねる (毎回の再生成で無限ループしないよう依存に含めない)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowAxisKey, colAxisKey, granularity, view, splitByPolarity]);

  const counts = useMemo(
    () => computeAxisCounts(posts, toPrimaryAxis(rowAxis), evaluation, granularity),
    [posts, rowAxis, evaluation, granularity],
  );
  // 評価で分ける表示は、各項目をポジ / ネガ / 中立の棒に展開する (積み上げより大小を比べやすい)。
  const barAxes = useMemo(
    () =>
      splitByPolarity ? expandAxisCountsByPolarity(counts, evaluation?.polarityLabels) : counts,
    [counts, splitByPolarity, evaluation],
  );
  const evaluationSummary = useMemo(
    () => computeEvaluationSummary(posts, evaluation),
    [posts, evaluation],
  );
  const matrix = useMemo(
    () => computeAxisMatrix(posts, rowAxis, colAxis, evaluation, granularity),
    [posts, rowAxis, colAxis, evaluation, granularity],
  );

  const selectClass = 'rounded border border-slate-300 bg-white px-2 py-1 text-xs';

  return (
    <div className="grid grid-cols-1 gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-0 items-center gap-1 text-xs text-slate-600">
          集計する調査項目
          <select
            value={rowAxisKey ?? ''}
            onChange={(e) => setRowAxisKey(e.target.value || null)}
            className={selectClass}
            style={{ maxWidth: '100%' }}
          >
            {axes.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 items-center gap-1 text-xs text-slate-600">
          集計の細かさ
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as AxisGranularity)}
            className={selectClass}
            style={{ maxWidth: '100%' }}
          >
            {(Object.keys(AXIS_GRANULARITY_LABELS) as AxisGranularity[]).map((g) => (
              <option key={g} value={g}>
                {AXIS_GRANULARITY_LABELS[g]}
              </option>
            ))}
          </select>
        </label>
        {view === 'bar' && evaluation && (
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={splitByPolarity}
              onChange={(e) => setSplitByPolarity(e.target.checked)}
            />
            評価で分けて表示
          </label>
        )}
        {view === 'matrix' && (
          <label className="flex min-w-0 items-center gap-1 text-xs text-slate-600">
            掛け合わせる項目
            <select
              value={colAxisKey ?? ''}
              onChange={(e) => setColAxisKey(e.target.value || null)}
              className={selectClass}
              style={{ maxWidth: '100%' }}
            >
              {axes.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                  {a.key === evaluationAxisKey ? '（評価）' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="ml-auto flex items-center gap-1 text-xs">
          {(
            [
              ['bar', '棒グラフ'],
              ['matrix', 'マトリクス'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              className={`rounded border px-2 py-0.5 ${
                view === id
                  ? 'border-primary-400 bg-primary-50 text-primary-700'
                  : 'border-slate-300 bg-white text-slate-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <EvaluationSummaryLine
        summary={evaluationSummary}
        polarityLabels={evaluation?.polarityLabels}
      />
      {view === 'bar' ? (
        <AreaBarChart axes={barAxes} polarityLabels={evaluation?.polarityLabels} />
      ) : (
        <StreetSurveyAxisMatrix
          matrix={matrix}
          rowLabel={rowAxis?.label ?? '調査項目'}
          colLabel={colAxis?.label ?? '調査項目'}
        />
      )}
    </div>
  );
}
